import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from '@angular/router';
import { DatatableComponent, ColumnMode } from "@swimlane/ngx-datatable";
import { Subject } from "rxjs";
import moment from "moment";
import { FechamentoService } from 'app/services/fechamento.service';
import { BrokerService } from '@core/services/broker.service';
import { OperadoraService } from '@core/services/operadora.service';
import { FechamentoBrokerChipInterface } from 'app/module/fechamento-broker-chip-interface';

@Component({
	selector: 'app-relatorio-chips-importados',
	templateUrl: './relatorio-chips-importados.component.html',
	styleUrls: ['./relatorio-chips-importados.component.scss']
})
export class RelatorioChipsImportadosComponent implements OnInit {
	constructor(
		private route: ActivatedRoute,
		private fechamentoService: FechamentoService,
		private brokerService: BrokerService,
		private operadoraService: OperadoraService,
	) {
		this._unsubscribeAll = new Subject();
	}

	private _unsubscribeAll: Subject<any>;

	starting = false;
	exportando = false;
	erro: string = null;
	contentHeader: object;
	ColumnMode = ColumnMode;
	idFechamento: number;
	rows: Array<FechamentoBrokerChipInterface> | any = [];
	temp: Array<FechamentoBrokerChipInterface> | any = [];
	brokers: any = [];
	operadoras: any = [];
	resumoBrokers: Array<{ id_broker: any, broker: string, qtd: number, pct: number }> = [];

	filtroStatus: number = 0;
	filtroVinculo: number = 0;
	filtroNuncaConectou: boolean = false;
	filtroSemConexaoXDias: boolean = false;
	diasSemConexao: number = 30;
	filtroBroker: any = 0;
	filtroOperadora: any = 0;
	filtroIccid: string = '';
	filtroNumero: string = '';

	@ViewChild(DatatableComponent) table: DatatableComponent;

	ngOnInit(): void {
		this.idFechamento = this.route.snapshot.params.id_fechamento;
		this.starting = true;

		this.contentHeader = {
			headerTitle: "Chips Importados",
			actionButton: false,
			breadcrumb: {
				type: "",
				links: [
					{ name: "Início", isLink: false },
					{ name: "Fechamento", isLink: true, link: "/fechamento" },
					{ name: "Chips Importados", isLink: false },
				],
			},
		};

		this.fechamentoService.getRelatorioChipsImportados(this.idFechamento).subscribe(
			(res) => {
				this.rows = res.data || [];
				this.temp = this.rows;
				this.montarResumoBrokers();
				this.starting = false;
			},
			(error) => {
				console.log(error);
				if (error && error.status === 401) {
					this.erro = 'Sessão expirada. Faça login novamente.';
				} else if (error && error.error && error.error.message) {
					this.erro = Array.isArray(error.error.message) ? error.error.message.join(', ') : error.error.message;
				} else if (error && error.status) {
					this.erro = `Erro ao carregar os chips importados (HTTP ${error.status}).`;
				} else {
					this.erro = 'Erro ao carregar os chips importados.';
				}
				this.starting = false;
			}
		);

		this.brokerService.get().subscribe(
			(res: any) => { if (res) this.brokers = [...res].sort((a, b) => (a.broker || '').localeCompare(b.broker || '')); },
			(error) => console.log(error)
		);

		this.operadoraService.get().subscribe(
			(res: any) => { if (res) this.operadoras = [...res].sort((a, b) => (a.operadora || '').localeCompare(b.operadora || '')); },
			(error) => console.log(error)
		);
	}

	ngAfterContentChecked() {
		this.rows = [...this.rows];
	}

	// Totais por broker das linhas atualmente exibidas (acompanham os filtros);
	// so entram brokers com dados no resultado filtrado.
	montarResumoBrokers() {
		const porBroker = new Map<any, { id_broker: any, broker: string, qtd: number }>();
		for (const r of this.rows) {
			const atual = porBroker.get(r.id_broker);
			if (atual) atual.qtd++;
			else porBroker.set(r.id_broker, { id_broker: r.id_broker, broker: r.broker || 'Sem broker', qtd: 1 });
		}
		const total = this.rows.length || 1;
		this.resumoBrokers = Array.from(porBroker.values())
			.map((b) => ({ ...b, pct: (b.qtd / total) * 100 }))
			.sort((a, b) => b.qtd - a.qtd);
	}

	// Clique no indicador aplica/remove o filtro daquele broker (mantem o select sincronizado).
	filtrarPorBroker(id_broker) {
		this.filtroBroker = this.filtroBroker == id_broker ? 0 : id_broker;
		this.aplicarFiltros();
	}

	aplicarFiltros() {
		let result = this.temp;

		if (this.filtroStatus == 1) {
			result = result.filter((d) => d.status == 1);
		} else if (this.filtroStatus == 2) {
			result = result.filter((d) => d.status == 0);
		}

		if (this.filtroVinculo == 1) {
			result = result.filter((d) => d.id_cliente != null);
		} else if (this.filtroVinculo == 2) {
			result = result.filter((d) => d.id_cliente == null);
		}

		// Conexao: as duas opcoes podem ser combinadas (uniao): "nunca conectou" OU "sem conectar ha mais de X dias"
		if (this.filtroNuncaConectou || this.filtroSemConexaoXDias) {
			const limite = moment().subtract(this.diasSemConexao || 0, 'days');
			result = result.filter((d) =>
				(this.filtroNuncaConectou && !d.dt_conexao) ||
				(this.filtroSemConexaoXDias && d.dt_conexao && moment(d.dt_conexao).isBefore(limite))
			);
		}

		// "!= 0" (e nao truthiness): o <select> devolve a string "0" para Todos, que e truthy
		if (this.filtroBroker != 0) {
			result = result.filter((d) => d.id_broker == this.filtroBroker);
		}

		if (this.filtroOperadora != 0) {
			result = result.filter((d) => d.id_operadora == this.filtroOperadora);
		}

		if (this.filtroIccid) {
			const val = this.filtroIccid.toLowerCase();
			result = result.filter((d) => d.iccid && d.iccid.toLowerCase().indexOf(val) !== -1);
		}

		if (this.filtroNumero) {
			const val = this.filtroNumero.toLowerCase();
			result = result.filter((d) => d.numero && d.numero.toLowerCase().indexOf(val) !== -1);
		}

		this.rows = result;
		this.montarResumoBrokers();
		if (this.table) this.table.offset = 0;
	}

	limparFiltros() {
		this.filtroStatus = 0;
		this.filtroVinculo = 0;
		this.filtroNuncaConectou = false;
		this.filtroSemConexaoXDias = false;
		this.diasSemConexao = 30;
		this.filtroBroker = 0;
		this.filtroOperadora = 0;
		this.filtroIccid = '';
		this.filtroNumero = '';
		this.rows = this.temp;
		this.montarResumoBrokers();
		if (this.table) this.table.offset = 0;
	}

	// Exporta exatamente o que esta na tabela (this.rows = dados ja filtrados) para .xlsx.
	// A biblioteca xlsx e carregada sob demanda para nao pesar o bundle principal.
	async exportarExcel() {
		if (this.exportando || !this.rows || this.rows.length == 0) return;
		this.exportando = true;
		try {
			const XLSX = await import('xlsx');
			const fmtData = (d, comHora = false) => d ? moment.utc(d).format(comHora ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY') : '';

			const linhas = this.rows.map((r) => ({
				'ICCID': r.iccid || '',
				'Número': r.numero || '',
				'Status': r.status == 1 ? 'Ativo' : 'Cancelado',
				'Status (broker)': r.descricao_status || '',
				'Vínculo': r.id_cliente ? 'Vinculado' : 'Não vinculado',
				'ID Cliente': r.id_cliente ? String(r.id_cliente) : '',
				'Cliente': r.id_cliente ? (r.nome_fantasia || r.razao_social || '') : '',
				'Broker': r.broker || '',
				'Operadora': r.operadora || '',
				'Dt. Ativação': fmtData(r.dt_ativacao),
				'Dt. Conexão': r.dt_conexao ? fmtData(r.dt_conexao) : 'Nunca conectou',
				'Dt. Importação': fmtData(r.dt_insert, true),
			}));

			const wsDados = XLSX.utils.json_to_sheet(linhas);
			wsDados['!cols'] = [22, 16, 10, 22, 14, 10, 34, 12, 14, 13, 15, 17].map((w) => ({ wch: w }));

			const brokerSel = this.brokers.find((b) => b.id_broker == this.filtroBroker);
			const operadoraSel = this.operadoras.find((o) => o.id_operadora == this.filtroOperadora);
			const partesConexao = [];
			if (this.filtroNuncaConectou) partesConexao.push('Nunca conectou');
			if (this.filtroSemConexaoXDias) partesConexao.push(`Mais de ${this.diasSemConexao} dias sem conectar`);
			const conexaoDesc = partesConexao.length ? partesConexao.join(' OU ') : 'Todos';
			const wsFiltros = XLSX.utils.aoa_to_sheet([
				['Filtro', 'Valor'],
				['Fechamento', String(this.idFechamento)],
				['Status', this.filtroStatus == 1 ? 'Ativo' : this.filtroStatus == 2 ? 'Cancelado' : 'Todos'],
				['Vínculo com cliente', this.filtroVinculo == 1 ? 'Vinculado' : this.filtroVinculo == 2 ? 'Não vinculado' : 'Todos'],
				['Conexão', conexaoDesc],
				['Broker', brokerSel ? brokerSel.broker : 'Todos'],
				['Operadora', operadoraSel ? operadoraSel.operadora : 'Todas'],
				['ICCID contém', this.filtroIccid || ''],
				['Número contém', this.filtroNumero || ''],
				['Registros exportados', String(linhas.length)],
				['Gerado em', moment().format('DD/MM/YYYY HH:mm')],
			]);
			wsFiltros['!cols'] = [{ wch: 22 }, { wch: 34 }];

			const wb = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(wb, wsDados, 'Chips Importados');
			XLSX.utils.book_append_sheet(wb, wsFiltros, 'Filtros');

			const nomeArquivo = `chips-importados-fechamento-${this.idFechamento}-${moment().format('YYYY-MM-DD')}.xlsx`;
			XLSX.writeFile(wb, nomeArquivo, { compression: true });
		} catch (e) {
			console.log(e);
			this.erro = 'Erro ao gerar o arquivo Excel.';
		} finally {
			this.exportando = false;
		}
	}
}
